import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User, Lock, Eye, EyeOff, Check } from 'lucide-react';
import { authService } from '../auth';
import toast from 'react-hot-toast';

const SettingsModal = ({ isOpen, onClose }) => {
  const [userInfo, setUserInfo] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isChanging, setIsChanging] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // localStorage 우선 로드 (즉시 표시)
      try {
        const stored = localStorage.getItem('userInfo');
        if (stored) setUserInfo(JSON.parse(stored));
      } catch (e) {}

      // Cognito에서 최신 정보 가져오기
      authService.getCurrentUser().then(user => {
        if (user) {
          setUserInfo(user);
          localStorage.setItem('userInfo', JSON.stringify(user));
        }
      }).catch(() => {});
    }
  }, [isOpen]);

  const resetPasswordFields = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowCurrent(false);
    setShowNew(false);
    setShowConfirm(false);
  };

  const handleClose = () => {
    resetPasswordFields();
    onClose();
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters.');
      return;
    }

    setIsChanging(true);
    try {
      const result = await authService.changePassword(currentPassword, newPassword);
      if (result.success) {
        toast.success('Password changed successfully.');
        resetPasswordFields();
      } else {
        toast.error(result.error || 'Failed to change password.');
      }
    } finally {
      setIsChanging(false);
    }
  };

  const getUserInitial = () => {
    if (userInfo?.name) return userInfo.name.charAt(0).toUpperCase();
    if (userInfo?.email) return userInfo.email.charAt(0).toUpperCase();
    return 'U';
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/50 z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ duration: 0.18, ease: [0.2, 0, 0.2, 1] }}
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50
              w-full max-w-md bg-bg-100 rounded-2xl shadow-2xl border border-bg-300/30 overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-bg-300/20">
              <h2 className="text-base font-semibold text-text-100">Settings</h2>
              <button
                onClick={handleClose}
                className="w-7 h-7 flex items-center justify-center rounded-lg text-text-400 hover:text-text-200 hover:bg-bg-300/50 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-bg-300/20 px-6">
              <button
                onClick={() => setActiveTab('profile')}
                className={`flex items-center gap-2 px-1 py-3 text-sm font-medium border-b-2 transition-colors mr-6 ${
                  activeTab === 'profile'
                    ? 'border-sedaily-500 text-sedaily-500'
                    : 'border-transparent text-text-400 hover:text-text-200'
                }`}
              >
                <User className="w-4 h-4" />
                Profile
              </button>
              <button
                onClick={() => setActiveTab('password')}
                className={`flex items-center gap-2 px-1 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'password'
                    ? 'border-sedaily-500 text-sedaily-500'
                    : 'border-transparent text-text-400 hover:text-text-200'
                }`}
              >
                <Lock className="w-4 h-4" />
                Password
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {activeTab === 'profile' && (
                <div className="space-y-5">
                  {/* Avatar */}
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-sedaily-500 flex items-center justify-center text-white text-xl font-semibold flex-shrink-0">
                      {getUserInitial()}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-text-100">
                        {userInfo?.name || userInfo?.email?.split('@')[0] || 'User'}
                      </div>
                      <div className="text-xs text-text-400 mt-0.5">{userInfo?.email || '—'}</div>
                    </div>
                  </div>

                  {/* Fields */}
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs font-medium text-text-400 mb-1.5 block">Name</label>
                      <div className="px-3 py-2.5 rounded-lg bg-bg-200/60 border border-bg-300/30 text-sm text-text-200">
                        {userInfo?.name || '—'}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-text-400 mb-1.5 block">Email</label>
                      <div className="px-3 py-2.5 rounded-lg bg-bg-200/60 border border-bg-300/30 text-sm text-text-200">
                        {userInfo?.email || '—'}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'password' && (
                <form onSubmit={handleChangePassword} className="space-y-4">
                  {[
                    { label: 'Current password', value: currentPassword, setter: setCurrentPassword, show: showCurrent, toggle: () => setShowCurrent(v => !v) },
                    { label: 'New password', value: newPassword, setter: setNewPassword, show: showNew, toggle: () => setShowNew(v => !v) },
                    { label: 'Confirm new password', value: confirmPassword, setter: setConfirmPassword, show: showConfirm, toggle: () => setShowConfirm(v => !v) },
                  ].map(({ label, value, setter, show, toggle }) => (
                    <div key={label}>
                      <label className="text-xs font-medium text-text-400 mb-1.5 block">{label}</label>
                      <div className="relative">
                        <input
                          type={show ? 'text' : 'password'}
                          value={value}
                          onChange={e => setter(e.target.value)}
                          required
                          className="w-full px-3 py-2.5 pr-10 rounded-lg bg-bg-200/60 border border-bg-300/30 text-sm text-text-100 outline-none focus:ring-1 focus:ring-sedaily-500/40 transition-colors placeholder:text-text-500"
                        />
                        <button
                          type="button"
                          onClick={toggle}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-text-400 hover:text-text-200 transition-colors"
                        >
                          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  ))}

                  <button
                    type="submit"
                    disabled={isChanging || !currentPassword || !newPassword || !confirmPassword}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-sedaily-500 text-white text-sm font-medium hover:bg-sedaily-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors mt-2"
                  >
                    {isChanging ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                    {isChanging ? 'Changing...' : 'Change password'}
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SettingsModal;
